import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEventStore, useGuestStore } from "store";
import * as SpacesService from "libs/digitalocean/SpacesService";
import { TGuest } from "services/types";
import CdtAppPublicApi from "cdt-app-public-api/dist";
import { FormattedMessage, useIntl } from "react-intl";
import { CameraOn, Close, User } from "assets/svg";
import ImageCropper from "./ImageCropper";
import BaseModal from "./BaseModal";

const IMAGE_SIZE_LIMIT = 10 * 1024 * 1024;

interface GuestFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (guest: TGuest) => void;
}

export default function GuestFormModal({
  isOpen,
  onClose,
  onSuccess,
}: GuestFormModalProps) {
  const { event, theme } = useEventStore();
  const { guest, setGuest } = useGuestStore();
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [guestName, setGuestName] = useState("");
  const [photoUploadStatus, setPhotoUploadStatus] = useState<
    "idle" | "uploading" | "success"
  >("idle");
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [hasExistingPhoto, setHasExistingPhoto] = useState(false);
  const [error, setError] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const showSkipOption = useMemo(() => {
    return !guestName;
  }, [guestName]);

  const photoStoragePath = useMemo(() => {
    return `guests/${event?.id}/${guest?.id}.jpg`;
  }, [event?.id, guest?.id]);

  useEffect(() => {
    if (!isOpen) {
      setGuestName("");
      setSelectedPhoto(null);
      setPhotoPreview(null);
      setPhotoUploadStatus("idle");
      setShowCropper(false);
      setHasExistingPhoto(false);
      setError("");
      setPendingPhoto(null);
      setHasUnsavedChanges(false);
      setShowUnsavedWarning(false);
      return;
    }

    if (!guest?.id) {
      setError("Guest not found. Please try again.");
      return;
    }

    setGuestName(guest.name || "");

    if (guest.photoStoragePath) {
      setHasExistingPhoto(true);
      setPhotoUploadStatus("success");

      const loadExistingPhoto = async () => {
        try {
          const photoUrl = await SpacesService.getFileUrl(
            guest.photoStoragePath as string
          );
          setPhotoPreview(photoUrl);
        } catch (err) {
          console.error("Failed to load existing photo preview:", err);
          setHasExistingPhoto(false);
          setPhotoUploadStatus("idle");
        }
      };

      loadExistingPhoto();
    } else {
      setHasExistingPhoto(false);
      setPhotoUploadStatus("idle");
      setPhotoPreview(null);
    }
  }, [isOpen, guest]);

  const updateGuest = useCallback(async (id: string, guestData: any) => {
    const cdtAppClient = new CdtAppPublicApi();
    return await cdtAppClient.updateGuest(id, guestData);
  }, []);

  const handlePhotoButton = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      fileInputRef.current?.click();
      e?.preventDefault();
    },
    []
  );

  const handlePhotoSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        setError(
          intl.formatMessage({
            id: "error.guestForm.invalidFileType",
            defaultMessage: "Por favor, selecione um arquivo de imagem válido",
          })
        );
        return;
      }
      if (file.size > IMAGE_SIZE_LIMIT) {
        setError(
          intl.formatMessage({
            id: "error.guestForm.fileTooLarge",
            defaultMessage: "O arquivo deve ter no máximo 10MB",
          })
        );
        return;
      }
      setSelectedPhoto(file);
      setError("");
      setShowCropper(true);
    },
    [intl]
  );

  const handleCropFinish = useCallback(async (croppedFile: File) => {
    setShowCropper(false);

    const previewUrl = URL.createObjectURL(croppedFile);
    setPhotoPreview(previewUrl);
    setHasExistingPhoto(false);
    setPendingPhoto(croppedFile);
    setHasUnsavedChanges(true);
  }, []);

  const handleCropCancel = useCallback(() => {
    setShowCropper(false);
    setSelectedPhoto(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleRemovePhoto = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setSelectedPhoto(null);
      setPhotoPreview(null);
      setHasExistingPhoto(false);
      setPendingPhoto(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (photoPreview && !hasExistingPhoto) {
        URL.revokeObjectURL(photoPreview);
      }

      setHasUnsavedChanges(true);

      setPhotoUploadStatus("idle");
    },
    [photoPreview, hasExistingPhoto]
  );

  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();
      setError("");

      if (!guest?.id) {
        setError("Guest not found. Please try again.");
        return;
      }

      if (!guestName.trim()) {
        const message = intl.formatMessage({
          id: "message.guest.nameRequired",
          defaultMessage: "Nome é obrigatório",
        });
        setError(message);
        return;
      }

      setPhotoUploadStatus("uploading");

      try {
        let photoStoragePathToSave = guest.photoStoragePath;
        const photoWasRemoved = guest.photoStoragePath && !photoPreview;

        if (photoWasRemoved) {
          photoStoragePathToSave = null;
        } else if (pendingPhoto) {
          await SpacesService.uploadFile(pendingPhoto, photoStoragePath);
          photoStoragePathToSave = photoStoragePath;
        }

        const updatedGuestData: Partial<TGuest> = {
          id: guest.id,
          eventId: event?.id as string,
          eventCode: event?.code as string,
          name: guestName.trim(),
          photoStoragePath: photoStoragePathToSave,
        };

        const guestResult = (await updateGuest(
          guest.id,
          updatedGuestData
        )) as TGuest;

        setGuest(guestResult);
        setPhotoUploadStatus("success");
        setPendingPhoto(null);
        setHasUnsavedChanges(false);

        if (onSuccess) {
          onSuccess(guestResult);
        }

        onClose();
      } catch (error) {
        console.error("Update failed:", error);
        setPhotoUploadStatus("idle");
        setError(
          intl.formatMessage({
            id: "error.guestForm.updateFailed",
            defaultMessage: "Falha ao salvar. Tente novamente.",
          })
        );
      }
    },
    [
      guestName,
      guest,
      event?.code,
      event?.id,
      setGuest,
      intl,
      updateGuest,
      onSuccess,
      onClose,
      pendingPhoto,
      photoStoragePath,
      photoPreview,
    ]
  );

  const handleSkip = () => {
    onClose();
  };

  const onGuestNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setGuestName(e.target.value);
    },
    []
  );

  const hasChanges = useMemo(() => {
    const nameChanged = guestName.trim() !== (guest?.name || "");
    const photoAdded = pendingPhoto !== null;
    const photoRemoved = hasExistingPhoto && !photoPreview;

    return nameChanged || photoAdded || photoRemoved;
  }, [pendingPhoto, guestName, guest?.name, hasExistingPhoto, photoPreview]);

  useEffect(() => {
    setHasUnsavedChanges(hasChanges);
  }, [hasChanges]);

  const handleForceClose = useCallback(() => {
    setPendingPhoto(null);
    setHasUnsavedChanges(false);
    setShowUnsavedWarning(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowUnsavedWarning(false);
  }, []);

  const handleModalClose = useCallback(() => {
    if (photoUploadStatus === "uploading") return;

    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      return;
    }

    onClose();
  }, [onClose, photoUploadStatus, hasUnsavedChanges]);

  if (!isOpen) return null;

  if (showUnsavedWarning) {
    return (
      <BaseModal isOpen={isOpen} onClose={handleCancelClose}>
        <div className="flex flex-col items-center max-w-md w-[80vw] p-6">
          <h2 className="text-xl mb-4 text-center">
            <FormattedMessage
              id="title.guestForm.unsavedChanges"
              defaultMessage="Alterações não salvas"
            />
          </h2>
          <p className="text-center mb-6">
            <FormattedMessage
              id="message.guestForm.unsavedChanges"
              defaultMessage="Você tem alterações não salvas. Deseja sair sem salvar?"
            />
          </p>
          <div className="flex gap-3 w-full">
            <button
              onClick={handleCancelClose}
              className={`flex-1 ${
                theme.name === `rose` ? theme.button : theme.buttonSecondary
              }`}
            >
              <FormattedMessage
                id="button.guestForm.cancel"
                defaultMessage="Cancelar"
              />
            </button>
            <button
              onClick={handleForceClose}
              className={`${
                theme.name === `rose` ? theme.buttonSecondary : theme.button
              } flex-1>`}
            >
              <FormattedMessage
                id="button.guestForm.exitWithoutSaving"
                defaultMessage="Sair sem salvar"
              />
            </button>
          </div>
        </div>
      </BaseModal>
    );
  }

  if (showCropper && selectedPhoto) {
    return (
      <BaseModal isOpen={isOpen} onClose={handleModalClose}>
        <div
          className="flex flex-col items-center max-w-md w-[80vw]"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-xl mb-4">
            <FormattedMessage
              id="title.guestForm.cropPhoto"
              defaultMessage="Recortar Foto"
            />
          </h2>
          <ImageCropper
            file={selectedPhoto}
            onFinish={handleCropFinish}
            onCancel={handleCropCancel}
            maxZoom={6}
          />
        </div>
      </BaseModal>
    );
  }

  return (
    <BaseModal isOpen={isOpen} onClose={handleModalClose}>
      <div
        className="flex flex-col items-center max-w-md w-[80vw] relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center items-start w-full mb-0">
          <h1 className="text-3xl text-center font-semibold">
            <FormattedMessage
              id="title.guestForm"
              defaultMessage="Seu Perfil"
            />
          </h1>
        </div>

        <form className="flex flex-col gap-2 w-full">
          <div className="flex flex-col gap-0">
            <div className="flex flex-col items-center gap-2 mb-0 relative">
              {photoPreview ? (
                <>
                  <button
                    type="button"
                    onClick={handlePhotoButton}
                    className="p-0 relative"
                  >
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="text-sm absolute -top-5 -right-4"
                    >
                      <Close
                        width={24}
                        height={24}
                        color={"none"}
                        outline={"black"}
                      />
                    </button>
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
                    />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handlePhotoButton}
                  className="p-0"
                >
                  <div className="relative">
                    <div className="w-24 h-24 grow-0 shrink-0 rounded-full overflow-hidden border-2 border-gray-200 flex items-center justify-center">
                      <div className="w-36 h-36 flex items-center justify-center">
                        <div>
                          <User
                            width={36}
                            height={36}
                            color="none"
                            outline="#414141"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="w-8 h-8 p-1 absolute bottom-0 right-0 bg-white rounded-full flex items-center justify-center border-2 border-gray-300">
                      <CameraOn
                        width={24}
                        height={24}
                        color="#000"
                        outline="#fff"
                      />
                    </div>
                  </div>
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg, image/png, image/jpg"
              onChange={handlePhotoSelect}
              className="hidden"
              id="photo-upload"
            />
          </div>
          <input
            type="text"
            name="name"
            value={guestName}
            onChange={onGuestNameChange}
            placeholder={intl.formatMessage({
              id: "placeholder.guestForm.name",
              defaultMessage: "Seu nome ou apelido",
            })}
            className={`w-full px-4 py-3 ${theme.input} flex items-center justify-between mt-4`}
            required
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className={`${theme.card} card w-full p-4`}>
            <p className="text-sm">
              <FormattedMessage
                id="message.guestForm.dataInfo"
                defaultMessage="Os dados fornecidos não são obrigatórios. Eles serão exibidos apenas como uma assinatura nas fotos e vídeos que você enviar."
              />
            </p>
          </div>
          <button
            type="submit"
            className={`w-full px-4 py-3 ${theme.buttonSecondary} flex items-center justify-between`}
            onClick={handleSubmit}
            disabled={photoUploadStatus === "uploading"}
          >
            <span className="w-full text-center text-lg no-select">
              {photoUploadStatus === "uploading" ? (
                <FormattedMessage
                  id="button.guestForm.uploading"
                  defaultMessage="Salvando..."
                />
              ) : (
                <FormattedMessage
                  id="button.guestForm.submit"
                  defaultMessage="Salvar"
                />
              )}
            </span>
          </button>

          {showSkipOption && (
            <button
              type="button"
              className="px-4 underline text-sm"
              onClick={handleSkip}
              disabled={photoUploadStatus === "uploading"}
            >
              <FormattedMessage
                id="button.guestForm.skip"
                defaultMessage="Continuar de forma anônima"
              />
            </button>
          )}
        </form>
      </div>
    </BaseModal>
  );
}
